import styled from 'styled-components';
import { motion } from 'framer-motion';

export const Page = styled.div`
  padding: 2.5rem 2rem 4rem;
  max-width: 1240px;
  margin: 0 auto;
  font-family: 'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif;

  @media (max-width: 768px) { padding: 1.5rem 1rem 3rem; }
  @media (max-width: 480px) { padding: 1.25rem 0.875rem 2.5rem; }
`;

/* ---------- Hero ---------- */

export const Hero = styled.section`
  margin-bottom: 2.5rem;
`;

export const HeroDivider = styled.div`
  height: 1px;
  background: var(--border-color);
  margin: 2rem 0 2.5rem 0;
`;

export const HeroEyebrow = styled.div`
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.075em;
  text-transform: uppercase;
  color: var(--text-muted);
  margin-bottom: 0.75rem;
`;

export const HeroTitle = styled.h1`
  font-family: 'Inter', system-ui, sans-serif;
  font-size: clamp(1.5rem, 2vw + 0.65rem, 2rem);
  font-weight: 700;
  letter-spacing: -0.03em;
  color: var(--text-primary);
  margin: 0 0 0.75rem 0;
  line-height: 1.1;
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    font-size: clamp(1.25rem, 2vw + 0.45rem, 1.6rem);
  }
`;

export const HeroGreetingAr = styled.span`
  font-family: 'Noto Sans Arabic', 'Amiri', 'Scheherazade New', 'Arial Unicode MS', sans-serif;
  font-weight: 600;
  font-size: 1em;
  letter-spacing: 0;
  direction: rtl;
  unicode-bidi: isolate;
  display: inline;
`;

export const HeroGreetingName = styled.span`
  font-weight: 500;
  color: var(--text-secondary);
  letter-spacing: -0.015em;
  display: inline;
`;

export const HeroSub = styled.p`
  font-size: 1rem;
  color: var(--text-muted);
  margin: 0;
  line-height: 1.5;
  max-width: none;
  white-space: nowrap;

  @media (max-width: 768px) {
    white-space: normal;
  }
`;

/* ---------- KPI Strip ---------- */

export const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  margin-bottom: 2.75rem;

  @media (max-width: 900px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 480px) { grid-template-columns: 1fr 1fr; gap: 0.625rem; }
`;

export const Kpi = styled(motion.div)`
  position: relative;
  background: var(--background-primary);
  border: 2px solid ${(p) => p.$accentColor || 'var(--border-color)'};
  border-radius: 12px;
  padding: 1.25rem 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.04);
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease, background 0.2s ease;
  overflow: hidden;

  /* Bold accent strip along the left edge */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    width: 4px;
    background: ${(p) => p.$accent || 'transparent'};
    opacity: 1;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12), 0 0 0 1px ${(p) => p.$accentColor || 'var(--border-color)'};
    border-color: ${(p) => p.$accentColor || 'rgba(184, 149, 106, 0.6)'};
    background: ${(p) => p.$accentTint || 'var(--background-primary)'};
  }

  @media (max-width: 480px) { padding: 1rem 1rem; }
`;

export const KpiLabel = styled.div`
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--text-muted);
  margin-bottom: 0.625rem;
  display: inline-flex;
  align-items: center;
  gap: 7px;
`;

export const KpiSwatch = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 7px;
  background: ${(p) => p.$tint};
  color: ${(p) => p.$color};
  font-weight: 700;
`;

export const KpiValue = styled.div`
  font-size: clamp(1.75rem, 2vw + 1rem, 2.25rem);
  font-weight: 700;
  letter-spacing: -0.03em;
  color: var(--text-primary);
  line-height: 1;
  font-variant-numeric: tabular-nums;
  margin-bottom: 0.5rem;
`;

export const KpiCaption = styled.div`
  margin-top: 0.5rem;
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--text-muted);
  line-height: 1.4;
`;

/* ---------- Section heading ---------- */

